// unstated is deprecated; new banner state belongs in src/bannerStore.ts
// eslint-disable-next-line no-restricted-imports
import { Container } from 'unstated';

type Listener = () => unknown;

type StateUpdater<S extends object> =
	// `undefined` is how callers signal "nothing changed", which skips the notification entirely.
	((prevState: Readonly<S>) => Partial<S> | S | null | undefined) | Partial<S> | S | null;

/**
 * Shared base for the banner containers.
 *
 * Reproduces unstated's `setState` semantics exactly — one microtask, and one notification, per
 * call — but holds listeners in a Set. unstated stores them in an array and `Subscribe`
 * unsubscribes/resubscribes on *every* render, so with an array each notification round costs
 * O(listeners²); a Set makes the resubscribe O(1).
 *
 * Subclasses that maintain state somewhere other than `this.state` (`BannerStateContainer` mirrors
 * `bannerStore`) call `notifyListeners` directly once they have mirrored the new value.
 */
export class BaseStateContainer<S extends object> extends Container<S> {
	private listeners: Set<Listener> = new Set();

	subscribe(fn: Listener): void {
		this.listeners.add(fn);
	}

	unsubscribe(fn: Listener): void {
		this.listeners.delete(fn);
	}

	setState(updater: StateUpdater<S>, callback?: () => void): Promise<void> {
		return Promise.resolve().then(() => {
			const next = typeof updater === 'function' ? updater(this.state) : updater;
			if (next == null) {
				callback?.();
				return;
			}

			this.state = Object.assign({}, this.state, next) as S;
			return this.notifyListeners().then(() => {
				callback?.();
			});
		});
	}

	protected notifyListeners(): Promise<void> {
		// Snapshot before notifying: a listener's re-render makes unstated's Subscribe unsubscribe
		// and resubscribe, which would mutate the Set mid-iteration.
		const snapshot = Array.from(this.listeners);
		return Promise.all(snapshot.map((listener) => listener())).then(() => undefined);
	}
}
